import React, { forwardRef, useEffect, useState } from "react";
import styled from "styled-components";
import { navigateWithInitiateCheckout } from "../services/checkoutTracking";
import { User } from "../types";
import { sha256 } from "js-sha256";
import { LOCAL_STORAGE_SESSION_IDENTITY_SESSION_ID_KEY } from "../hooks/useAnswerTracker";
import { Model } from "survey-core";
import { CheckoutPlan } from "../LandingPage";
import * as amplitude from "@amplitude/analytics-browser";
import { decorateUrlWithAttributionParams } from "../services/checkoutAttribution";

interface PlanCardProps {
  $isSelected: boolean;
  $hasBadge: boolean;
}

interface CheckoutButtonProps {
  $isDisabled: boolean;
}

const SectionContainer = styled.section`
  text-align: center;
  padding: 14px;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  margin-top: 24px;

  @media (min-width: 768px) {
    padding: 20px;
    margin-top: 100px;
  }
`;

const Title = styled.h2`
  font-size: clamp(1.8rem, 4vw, 2.5rem);
  color: #1e1e1e;
  padding: 0;
  margin: 0;
  font-family: Oswald, sans-serif;
  font-size: 23px;
  font-weight: 600;
  max-width: 950px;

  @media (min-width: 768px) {
    font-size: 40px;
  }
`;

const TitleSpan = styled.div`
  color: #0b6dff;
`;

const Subtitle = styled.p`
  font-family: "Lato";
  font-size: 24px;
  color: #4a4a4a;
  font-weight: 500;
  margin-top: 6px;
  margin-bottom: 40px;
  max-width: 900px;
  line-height: 1.4;

  @media (max-width: 800px) {
    font-size: 14px;
  }

  @media (min-width: 768px) {
    margin-top: 6px;
    margin-bottom: 20px;
    font-size: 20px;
  }
`;

const PlansWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 50px;
  flex-wrap: wrap;
  margin-bottom: 30px;
  width: 100%;
  max-width: 880px;

  @media (max-width: 800px) {
    flex-direction: column-reverse;
    padding-left: 10px;
    pading-right: 10px;
    gap: 30px;
    align-items: center;
  }
`;

const PlanCard = styled.div<PlanCardProps>`
  width: 100%;
  box-sizing: border-box;
  background-color: ${(props) => (props.$isSelected ? "#F8FBFF" : "#ffffff")};
  border: 4px solid ${(props) => (props.$isSelected ? "#E7F0FF" : "white")};
  border-radius: 20px;
  padding: 36px 24px;
  box-shadow: ${(props) =>
    props.$isSelected
      ? "0 10px 25px rgba(64, 109, 223, 0.16)"
      : "0 5px 15px rgba(0, 0, 0, 0.08)"};
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  text-align: center;
  display: flex;
  flex-direction: column;

  &:focus {
    outline: 2px solid rgb(64, 110, 223);
    outline-offset: 3px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  @media (min-width: 768px) {
    padding: 28px;
  }
`;

const PlanBadgeWeb13 = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  gap: 10px;
  background: #e7f0ff;
  border-radius: 95.5168px;
  font-family: "Oswald";
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 17px;
  color: #0b6dff;

  @media (min-width: 768px) {
    font-size: 18px;
    line-height: 22px;
  }
`;

const PlanIcon = styled.div`
  font-size: 3em;
  margin-bottom: 0;
  color: #007bff;
  line-height: 1;
  width: 36px;
  height: 36px;

  @media (min-width: 768px) {
    width: 48px;
    height: 48px;
  }

  & > svg {
    width: 100%;
    height: 100%;
  }
`;

const PlanIconPlus = styled.div`
  color: #007bff;
  line-height: 1;
`;

const CardTitle = styled.h3<PlanCardProps>`
  margin-top: 16px;
  margin-bottom: 16px;
  font-family: "Lato";
  font-style: normal;
  font-weight: 800;
  font-size: 28px;
  line-height: 120%;
  text-align: center;
  letter-spacing: 0.01em;
  color: #1a1a1a;

  @media (min-width: 768px) {
    font-size: 42px;
    margin-bottom: 24px;
  }
`;

const PriceWrap = styled.div`
  margin-top: 20px;

  @media (min-width: 768px) {
    align-items: center;
    justify-content: center;
    font-size: 24px;
    margin-top: 24px;
  }
`;

const PlanPrice = styled.div`
  font-family: "Lato";
  font-style: normal;
  font-weight: 700;
  font-size: 36px;
  line-height: 34px;
  text-align: center;
  color: #0b6dff;
  margin-top: 8px;

  @media (min-width: 768px) {
    font-size: 48px;
    line-height: 43px;
  }
`;

const PriceAmount = styled.span`
  margin-right: 5px;
`;

const PriceDetails = styled.span`
  font-size: 16px;
  color: #7A7A7A;
  @media (min-width: 768px) {
    font-size: 20px;
  }
`;

const PlanBillingDetails = styled.p`
  font-family: "Lato";
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 15px;
  text-align: center;
  color: #7a7a7a;
  margin-top: 8px;
  margin-bottom: 0;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 16px;
    line-height: 19px;
  }
`;

const PlanDescription = styled.p`
  font-family: "Lato";
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 21px;
  text-align: center;
  color: #7a7a7a;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 18px;
    line-height: 150%;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 16px;
  width: 100%;

  @media (min-width: 768px) {
    margin-top: 24px;
  }
`;

export const CheckoutButton = styled.a<CheckoutButtonProps>`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 12px 0;
  background: #dfb140;
  border-radius: 8px;
  font-family: "Oswald";
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 37px;
  text-align: center;
  color: #000000;
  text-transform: uppercase;
  text-decoration: none;
  display: inline-block;
  transition: background-color 0.3s ease, opacity 0.3s ease, transform 0.2s ease;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  opacity: ${(props) => (props.$isDisabled ? 0.6 : 1)};
  cursor: ${(props) => (props.$isDisabled ? "not-allowed" : "pointer")};
  pointer-events: ${(props) => (props.$isDisabled ? "none" : "auto")};

  @media (min-width: 768px) {
    font-size: 32px;
    line-height: 48px;
    padding: 16px 0;
  }

  &:hover {
    background-color: rgb(64, 110, 223);
    transform: ${(props) => (!props.$isDisabled ? "translateY(-2px)" : "none")};
    color: white;
    box-shadow: ${(props) =>
      !props.$isDisabled
        ? "0 6px 12px rgba(0, 0, 0, 0.15)"
        : "0 4px 10px rgba(0, 0, 0, 0.1)"};
  }

  &:focus {
    outline: 2px solid #dfb140;
    outline-offset: 3px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }
`;

const TrialText = styled.p`
  font-family: "Lato";
  font-style: italic;
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  text-align: center;
  color: #4a4a4a;
  margin-top: 12px;

  @media (min-width: 768px) {
    font-size: 16px;
    line-height: 18px;
  }
`;

const Section1 = styled.div`
  display: flex;
  justify-content: center;
`;

const Section2 = styled.div``;

const Section4 = styled.div`
  @media (min-width: 768px) {
    padding-bottom: 16px;
  }
`;

const Section5 = styled.div``;
const Section6 = styled.div``;


const TrialOfferCard = styled.div`
  width: 100%;
  max-width: 560px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  padding: 16px;
  box-sizing: border-box;

  @media (min-width: 768px) {
    padding: 16px 32px;
    max-width: 80%;
  }

  & + & {
    margin-top: 8px;
    padding: 14px 16px;

    @media (min-width: 768px) {
      margin-top: 14px;
      padding: 14px 16px;
    }
  }
`;

const TrialTimeline = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 12px;
  padding-left: 21px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 32px;
    margin-bottom: 14px;
    padding-left: 0;
  }
`;

const TrialTimelineItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  position: relative;
  z-index: 1;

  @media (min-width: 768px) {
    flex: 1;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 8px;
  }
`;

const TrialTimelineConnector = styled.div<{ isSecond?: boolean }>`
  display: none;

  @media (min-width: 768px) {
    position: absolute;
    left: 50%;
    display: block;
    height: 6px;
    background-image: ${(props) =>
      props.isSecond
        ? "linear-gradient(to right, #418BFC, #8EB6F8)"
        : "linear-gradient(to right, #0B6DFF, #418BFC)"};
    margin-top: 20px;
    width: 100%;
  }
`;

const TrialTimelineIconWrapper = styled.div`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #0b6dff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-left: -22px;
  z-index: 1;

  @media (min-width: 768px) {
    margin-left: 0;
  }
`;

const TrialTimelineContent = styled.div`
  flex: 1;
  text-align: left;
`;

const TrialTimelineContentTop = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  justify-content: left;

  @media (min-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const TrialTimelineDatePill = styled.span`
  display: inline-block;
  background: #e7f0ff;
  color: #4a4a4a;
  font-family: "Lato";
  font-size: 14px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 999px;
  margin-left: 8px;

  @media (min-width: 768px) {
    margin-left: 0;
    margin-top: 4px;
    font-size: 14px;
  }
`;

const TrialTimelineTitle = styled.h4`
  font-family: "Lato";
  font-size: 17px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  display: inline;

  @media (min-width: 768px) {
    display: block;
    font-size: 22px;
    margin-top: 4px;
  }
`;

const TrialTimelineDescription = styled.p`
  font-family: "Lato";
  font-size: 13px;
  font-weight: 500;
  color: #7a7a7a;
  line-height: 1.35;
  margin: 3px 0 0 0;

  @media (min-width: 768px) {
    font-size: 14px;
    text-align: center;
    margin-top: 6px;
    max-width: 300px;
  }
`;

const TrialVerticalLine = styled.div<{ isSecond?: boolean }>`
  position: absolute;
  left: -3px;
  top: 40px;
  bottom: -30px;
  width: 6px;
  background: #0b6dff;
  z-index: 0;
  background-image: ${(props) =>
    props.isSecond
      ? "linear-gradient(to bottom, #418BFC, #8EB6F8)"
      : "linear-gradient(to bottom, #0B6DFF, #418BFC)"};

  @media (min-width: 768px) {
    display: none;
  }
`;

const TrialPricingSection = styled.div<{ $isFamilyPlan: boolean }>`
  text-align: ${(props) => (props.$isFamilyPlan ? "left" : "center")};
`;

const VsSeparatePlans = styled.div`
  border-top: 1px solid #F6F6F8;
  margin-top: 8px;
  padding-top: 8px;
  font-family: 'Lato';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 140%;
  color: #7A7A7A;
  text-align: left;

  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

const TrialPricingLabel = styled.p`
  font-family: "Lato";
  font-size: 12px;
  font-weight: 500;
  color: #7a7a7a;
  margin: 0 0 8px 0;

  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

const PricingWrapper = styled.div<{ $isFamilyPlan: boolean }>`
  display: flex;
  justify-content: ${(props) => (props.$isFamilyPlan ? "space-between" : "center")};

`;

const Pills = styled.div<{ $direction?: "column" | "row" }>`
  display: flex;
  flex-direction: ${(props) => props.$direction || "column"};
  gap: 8px;
  justify-content: center;
`;

// Spaced variant for the "2 MEMBERS / SAVE" pills on the view-all family cards,
// so they're not cramped against the price above and the CTA below.
const FamilyPlanPills = styled(Pills)`
  margin: 14px 0 8px;
`;

const pillThemes = {
  blue: {
    background: "#E7F0FF",
    color: "#0B6DFF",
  },
  green: {
    background: "#E3F5EA",
    color: "#317A40",
  },
  yellow: {
    background: "#FFFAE6",
    color: "#8D6F04",
  },
};

const Pill = styled.div<{ $theme: keyof typeof pillThemes }>`
/* chip */

/* Auto layout */
display: flex;
flex-direction: row;
justify-content: center;
align-items: center;
padding: 2px 8px;
gap: 8px;

background: ${(props) => pillThemes[props.$theme].background};
border-radius: 100px;

font-family: 'Lato';
font-style: normal;
font-weight: 700;
font-size: 10px;
line-height: 150%;
color: ${(props) => pillThemes[props.$theme].color};

@media (min-width: 768px) {
  font-size: 16px;
}
`;

const TrialPricingAmount = styled.span`
  font-family: "Lato";
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;

  @media (min-width: 768px) {
    font-size: 28px;
  }
`;

const TrialPricingDetail = styled.span`
  font-family: "Lato";
  font-size: 14px;
  font-weight: 500;
  color: #7a7a7a;

  @media (min-width: 768px) {
    font-size: 20px;
  }
`;

const FamilyPlanPricingDetail = styled.div`
font-family: 'Lato';
font-style: normal;
font-weight: 400;
font-size: 12px;
line-height: 140%;
margin-top: 8px;

color: #7A7A7A;

@media (min-width: 768px) {
  font-size: 16px;
}
`;

const TrialCtaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  padding: 0 16px;
  width: 100%;
  max-width: 560px;

  @media (min-width: 768px) {
    margin-top: 14px;
    gap: 14px;
    max-width: 350px;
  }
`;

const FamilyPlanToggle = styled.div<{ $active: boolean }>`
  width: 100%;
  max-width: 560px;
  @media (min-width: 768px) {
    max-width: 80%;
  }
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 16px;
  margin-bottom: 12px;
  border-radius: 10px;
  background: #E7F0FF;
  border: 1px solid #81AAEE;
  transition: background 0.2s ease, border-color 0.2s ease;

  @media (min-width: 768px) {
    padding: 14px 24px;
    margin-bottom: 32px;
  }
`;

const FamilyPlanLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
`;

const FamilyAvatarGroup = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const FamilyAvatar = styled.div<{ $variant: "you" | "partner" | "single" }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  z-index: 2;

  & + & {
    margin-left: -15px;
    z-index: 1;
  }

  @media (min-width: 768px) {
    width: 42px;
    height: 42px;
    font-size: 13px;
  }
`;

const FamilyPlanLabel = styled.span<{ $active: boolean }>`
  font-family: "Lato";
  font-weight: 600;
  font-size: 16px;
  color: ${(props) => (props.$active ? "#0B6DFF" : "#1A1A1A")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: 768px) {
    font-size: 20px;
  }
`;

const FamilyPlanAction = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font-family: "Lato";
  font-weight: ${(props) => (props.$active ? 500 : 700)};
  font-size: 14px;
  color: ${(props) => (props.$active ? "#7A7A7A" : "#0B6DFF")};
  text-decoration: ${(props) => (props.$active ? "none" : "underline")};
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    opacity: 0.85;
  }

  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

const TrialViewAllPlans = styled.button`
  font-family: "Lato";
  font-size: 14px;
  font-weight: 500;
  color: #7a7a7a;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  margin: 0;

  &:hover {
    color: #1a1a1a;
  }
`;

// "← Back to recommended plan" link under the view-all toggle — needs breathing room
// from the toggle above and the content below (esp. on mobile).
const BackToPlanLink = styled(TrialViewAllPlans)`
  margin-top: 20px;
  margin-bottom: 8px;

  @media (min-width: 768px) {
    margin-top: 24px;
  }
`;

/* web29_trial_toggle: discount display + trial/no-trial toggle */
const DiscountRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const PriceCluster = styled.span`
  display: inline;
`;


const SavingsLine = styled.p`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: "Lato";
  font-size: 14px;
  font-weight: 600;
  color: #317a40;
  margin: 16px 0 6px 0;

  @media (min-width: 768px) {
    font-size: 18px;
  }
`;

const TrialToggleRow = styled.div<{ $highlight: boolean }>`
  width: 100%;
  max-width: 560px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 16px;
  margin-top: 12px;
  border-radius: 12px;
  background: ${(props) => (props.$highlight ? "#E3F5EA" : "#ffffff")};
  border: 1px solid ${(props) => (props.$highlight ? "#43A047" : "#E5E7EB")};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  transition: background 0.2s ease, border-color 0.2s ease;

  @media (min-width: 768px) {
    max-width: 80%;
    padding: 14px 24px;
  }
`;

const TrialToggleLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  min-width: 0;
`;

const TrialToggleTagIcon = styled.div`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #c9ebd5;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TrialToggleTextWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const TrialToggleTitle = styled.span`
  font-family: "Lato";
  font-weight: 700;
  font-size: 15px;
  color: #1a1a1a;

  @media (min-width: 768px) {
    font-size: 18px;
  }
`;

const TrialToggleSubtitle = styled.span`
  font-family: "Lato";
  font-weight: 400;
  font-size: 13px;
  color: #7a7a7a;

  @media (min-width: 768px) {
    font-size: 15px;
  }
`;

const SwitchTrack = styled.button<{ $on: boolean }>`
  flex-shrink: 0;
  position: relative;
  width: 52px;
  height: 30px;
  border-radius: 100px;
  border: none;
  cursor: pointer;
  padding: 0;
  background: ${(props) => (props.$on ? "#2E9E4F" : "#C9CDD6")};
  transition: background 0.2s ease;
`;

const SwitchKnob = styled.span<{ $on: boolean }>`
  position: absolute;
  top: 3px;
  left: 3px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  transform: translateX(${(props) => (props.$on ? "22px" : "0")});
  transition: transform 0.2s ease;
`;

interface PricingPlansProps {
  plans: CheckoutPlan[];
  multiUserPlans: CheckoutPlan[];
  user?: User;
  survey?: Model;
  isTrialToggleVariation?: boolean;
  // web29_trial_toggle: optionally controlled by the parent (LandingPage) so the hero
  // CTA copy can stay in sync with the toggle. Falls back to internal state.
  isNoTrial?: boolean;
  setIsNoTrial?: (next: boolean) => void;
}

const PricingPlans = forwardRef<HTMLDivElement, PricingPlansProps>(({ plans, multiUserPlans, user, survey, isTrialToggleVariation = false, isNoTrial: isNoTrialProp, setIsNoTrial: setIsNoTrialProp }, ref) => {
  let isFamilyPlan = survey?.getValue("togetherConsistent") ===  "true";
  const [dummyState, setDummyState] = useState(0);

  // web29_trial_toggle: variant defaults to no-trial (pay now + 20% off); the toggle
  // flips to the standard 14-day free trial. Control never sees the toggle.
  const [isNoTrialLocal, setIsNoTrialLocal] = useState(isTrialToggleVariation);
  const isNoTrial = isNoTrialProp ?? isNoTrialLocal;
  const setIsNoTrial = setIsNoTrialProp ?? setIsNoTrialLocal;

  const handleTrialToggle = () => {
    const next = !isNoTrial;
    amplitude.track("TrialToggleChanged", { choice: next ? "no_trial" : "trial" });
    setIsNoTrial(next);
  };

  // 20% off, computed from the *displayed* price string so it's correct for both the
  // single plan ($15.99) and the gym-buddy per-person price ($13.38) — priceRaw holds
  // the single-plan amount even on family plans, so it can't be the discount base.
  const NO_TRIAL_DISCOUNT = 0.8;
  const applyDiscount = (value?: string, withDollar = true) => {
    const n = parseFloat(String(value ?? "").replace(/[^0-9.]/g, ""));
    if (isNaN(n)) return value ?? "";
    const v = (n * NO_TRIAL_DISCOUNT).toFixed(2);
    return withDollar ? `$${v}` : v;
  };
  const discountedPrice = (plan: CheckoutPlan) => applyDiscount(plan.price);

  // Total savings % shown for no-trial, computed from the actual prices so the family +
  // no-trial discount compounds correctly (NOT additively). Baseline is the regular
  // single-plan price for the tier (the "vs $X/mo each on separate plans" figure), so a
  // single plan yields the flat 20% and a gym-buddy plan yields the true combined number
  // (e.g. $10.70 vs $15.99 ≈ 33%, not 35%).
  const toNumber = (value?: string) => parseFloat(String(value ?? "").replace(/[^0-9.]/g, "")) || 0;
  const savingsPercent = (plan?: CheckoutPlan) => {
    const discounted = toNumber(applyDiscount(plan?.price, false));
    const single = isFamilyPlan ? plans.find((p) => p.id === plan?.id) : plan;
    const baseline = toNumber(single?.price);
    if (!discounted || !baseline) return 20;
    return Math.round((1 - discounted / baseline) * 100);
  };

  const toggleFamilyPlan = () => {
    const currentValue = survey?.getValue("togetherConsistent") ===  "true";
    const newValue = !currentValue;
    survey?.setValue("togetherConsistent", String(newValue));

    if (newValue) {
      amplitude.track("PricingPageGymBuddyAdded");
    } else {
      amplitude.track("PricingPageGymBuddyRemoved");
    }
    setDummyState(s=> s + 1);
  };

  useEffect(() => {
    if (isFamilyPlan) { 
      amplitude.track("PricingPageWithGymBuddyPlanAccepted");
    } else {
      amplitude.track("PricingPageWithoutGymBuddyPlanAccepted");
    }
  }, []);

  const getCheckoutUrl = (checkoutUrl: string, plan: CheckoutPlan) => {
    if (!plan.isOneTime) {

      return "/checkout?plan=" + plan.id + (plan.isMultiUser ? "&isMultiUser=true" : "");
    }

    if (localStorage.getItem("checkoutv2") !== "enabled") {
      return checkoutUrl;
    }

    const urlObj = new URL(checkoutUrl);

    if (
      urlObj.hostname === "api.builtwithscience.com" &&
      urlObj.pathname.startsWith("/production/gateway/v2/stripe/")
    ) {
      urlObj.hostname = urlObj.hostname.replace(
        "api.builtwithscience.com",
        "core.builtwithscience.com"
      );
      urlObj.pathname = urlObj.pathname.replace(
        "/production/gateway/v2/stripe/",
        "/internal/public/checkout/web/"
      );
    }

    if (localStorage.getItem("checkoutv2TestMode") === "enabled") {
      urlObj.hostname = urlObj.hostname.replace(
        "core.builtwithscience.com",
        "staging.core.builtwithscience.com"
      );
    }

    const iacConnectionId = localStorage.getItem("iacConnectionId");
    const mode = localStorage.getItem("mode");

    if (iacConnectionId && mode === "webview") {
      urlObj.pathname = urlObj.pathname.replace(
        "/internal/public/checkout/web/",
        "/internal/public/checkout/webview/"
      );
      urlObj.searchParams.set("connectionId", iacConnectionId);
    }

    if (user?.email) {
      urlObj.searchParams.set("email", sha256(user.email));
    }

    if (user?.name) {
      urlObj.searchParams.set("name", user.name);
    }

    const sessionIdentitySessionId = localStorage.getItem(
      LOCAL_STORAGE_SESSION_IDENTITY_SESSION_ID_KEY
    );

    if (sessionIdentitySessionId) {
      urlObj.searchParams.set(
        "session_identity_session_id",
        sessionIdentitySessionId
      );
    }

    return decorateUrlWithAttributionParams(urlObj).toString();
  };

  // Carry the no-trial choice into the internal checkout so it selects the
  // create-subscription (immediate, 20% off) endpoint instead of create-trial.
  const checkoutUrlFor = (plan: CheckoutPlan) => {
    const url = getCheckoutUrl(plan.checkoutUrl, plan);
    if (isNoTrial && url.startsWith("/checkout")) {
      return `${url}&trial=false`;
    }
    return url;
  };

  const [showSinglePlanCard, setShowSinglePlanCard] = useState(true);
  const yearlyPlan = isFamilyPlan ? multiUserPlans.find((plan) => plan.id === "committed") : plans.find((plan) => plan.id === "committed");

  if (!yearlyPlan) {
    return null;
  }

  const trialToggleRow = isTrialToggleVariation ? (
    <TrialToggleRow $highlight={!isNoTrial}>
      <TrialToggleLeft>
        {!isNoTrial && (
          <TrialToggleTagIcon>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20.59 13.41L11 3.83A2 2 0 0 0 9.59 3.24L4 3a1 1 0 0 0-1 1l.24 5.59A2 2 0 0 0 3.83 11l9.58 9.59a2 2 0 0 0 2.83 0l4.35-4.35a2 2 0 0 0 0-2.83Z" stroke="#317A40" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="7.5" cy="7.5" r="1.5" fill="#317A40" />
            </svg>
          </TrialToggleTagIcon>
        )}
        <TrialToggleTextWrap>
          <TrialToggleTitle>{isNoTrial ? "Not ready to commit?" : (isFamilyPlan ? "Want an additional 20% off?" : "Want 20% off instead?")}</TrialToggleTitle>
          <TrialToggleSubtitle>{isNoTrial ? "Try free for 14 days" : "Skip the trial to save now."}</TrialToggleSubtitle>
        </TrialToggleTextWrap>
      </TrialToggleLeft>
      <SwitchTrack $on={!isNoTrial} onClick={handleTrialToggle} type="button" aria-label="Toggle free trial">
        <SwitchKnob $on={!isNoTrial} />
      </SwitchTrack>
    </TrialToggleRow>
  ) : null;

  return (
    <SectionContainer id="pricingbox" ref={ref}>
      <Title>
        TRY YOUR PERSONAL PLAN <TitleSpan>{isNoTrial ? "RISK-FREE FOR 30 DAYS" : "FREE FOR 14 DAYS"}</TitleSpan>
      </Title>
      <Subtitle>
        {isNoTrial
          ? "Don't see real results in 30 days? Get every penny back. No questions asked."
          : "Cancel anytime with no charge before your trial ends"}
      </Subtitle>

      <FamilyPlanToggle $active={isFamilyPlan}>
        <FamilyPlanLeft>
          {isFamilyPlan ? (
            <FamilyAvatarGroup>
              <FamilyAvatar $variant="you">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0.75" y="0.75" width="30.5" height="30.5" rx="15.25" fill="white"/>
                <rect x="0.75" y="0.75" width="30.5" height="30.5" rx="15.25" stroke="#81AAEE" stroke-width="1.5"/>
                <path d="M11.149 17.64V21H9.54103V17.64L6.37903 12.324H7.80103C7.94103 12.324 8.05103 12.358 8.13103 12.426C8.21503 12.494 8.28503 12.58 8.34103 12.684L9.92503 15.582C10.017 15.754 10.097 15.916 10.165 16.068C10.237 16.22 10.301 16.372 10.357 16.524C10.409 16.372 10.469 16.22 10.537 16.068C10.605 15.916 10.683 15.754 10.771 15.582L12.349 12.684C12.393 12.596 12.459 12.514 12.547 12.438C12.635 12.362 12.747 12.324 12.883 12.324H14.311L11.149 17.64ZM16.3349 14.748C16.7909 14.748 17.2049 14.822 17.5769 14.97C17.9529 15.118 18.2729 15.328 18.5369 15.6C18.8049 15.872 19.0109 16.204 19.1549 16.596C19.2989 16.988 19.3709 17.426 19.3709 17.91C19.3709 18.398 19.2989 18.838 19.1549 19.23C19.0109 19.622 18.8049 19.956 18.5369 20.232C18.2729 20.508 17.9529 20.72 17.5769 20.868C17.2049 21.016 16.7909 21.09 16.3349 21.09C15.8749 21.09 15.4569 21.016 15.0809 20.868C14.7049 20.72 14.3829 20.508 14.1149 20.232C13.8509 19.956 13.6449 19.622 13.4969 19.23C13.3529 18.838 13.2809 18.398 13.2809 17.91C13.2809 17.426 13.3529 16.988 13.4969 16.596C13.6449 16.204 13.8509 15.872 14.1149 15.6C14.3829 15.328 14.7049 15.118 15.0809 14.97C15.4569 14.822 15.8749 14.748 16.3349 14.748ZM16.3349 19.95C16.8469 19.95 17.2249 19.778 17.4689 19.434C17.7169 19.09 17.8409 18.586 17.8409 17.922C17.8409 17.258 17.7169 16.752 17.4689 16.404C17.2249 16.056 16.8469 15.882 16.3349 15.882C15.8149 15.882 15.4289 16.058 15.1769 16.41C14.9289 16.758 14.8049 17.262 14.8049 17.922C14.8049 18.582 14.9289 19.086 15.1769 19.434C15.4289 19.778 15.8149 19.95 16.3349 19.95ZM25.8055 14.844V21H24.8995C24.7035 21 24.5795 20.91 24.5275 20.73L24.4255 20.238C24.1735 20.494 23.8955 20.702 23.5915 20.862C23.2875 21.018 22.9295 21.096 22.5175 21.096C22.1815 21.096 21.8835 21.04 21.6235 20.928C21.3675 20.812 21.1515 20.65 20.9755 20.442C20.7995 20.234 20.6655 19.988 20.5735 19.704C20.4855 19.416 20.4415 19.1 20.4415 18.756V14.844H21.9235V18.756C21.9235 19.132 22.0095 19.424 22.1815 19.632C22.3575 19.836 22.6195 19.938 22.9675 19.938C23.2235 19.938 23.4635 19.882 23.6875 19.77C23.9115 19.654 24.1235 19.496 24.3235 19.296V14.844H25.8055Z" fill="#0B6DFF"/>
              </svg>

              </FamilyAvatar>
              <FamilyAvatar $variant="partner" aria-hidden>
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0.75" y="0.75" width="30.5" height="30.5" rx="15.25" fill="white"/>
                  <rect x="0.75" y="0.75" width="30.5" height="30.5" rx="15.25" stroke="#C1C6D4" stroke-width="1.5"/>
                  <path d="M13.175 14.825C12.3917 14.0417 12 13.1 12 12C12 10.9 12.3917 9.95833 13.175 9.175C13.9583 8.39167 14.9 8 16 8C17.1 8 18.0417 8.39167 18.825 9.175C19.6083 9.95833 20 10.9 20 12C20 13.1 19.6083 14.0417 18.825 14.825C18.0417 15.6083 17.1 16 16 16C14.9 16 13.9583 15.6083 13.175 14.825ZM8 22V21.2C8 20.6333 8.146 20.1127 8.438 19.638C8.73 19.1633 9.11733 18.8007 9.6 18.55C10.6333 18.0333 11.6833 17.646 12.75 17.388C13.8167 17.13 14.9 17.0007 16 17C17.1 16.9993 18.1833 17.1287 19.25 17.388C20.3167 17.6473 21.3667 18.0347 22.4 18.55C22.8833 18.8 23.271 19.1627 23.563 19.638C23.855 20.1133 24.0007 20.634 24 21.2V22C24 22.55 23.8043 23.021 23.413 23.413C23.0217 23.805 22.5507 24.0007 22 24H10C9.45 24 8.97933 23.8043 8.588 23.413C8.19667 23.0217 8.00067 22.5507 8 22ZM10 22H22V21.2C22 21.0167 21.9543 20.85 21.863 20.7C21.7717 20.55 21.6507 20.4333 21.5 20.35C20.6 19.9 19.6917 19.5627 18.775 19.338C17.8583 19.1133 16.9333 19.0007 16 19C15.0667 18.9993 14.1417 19.112 13.225 19.338C12.3083 19.564 11.4 19.9013 10.5 20.35C10.35 20.4333 10.229 20.55 10.137 20.7C10.045 20.85 9.99933 21.0167 10 21.2V22ZM17.413 13.413C17.8043 13.021 18 12.55 18 12C18 11.45 17.8043 10.9793 17.413 10.588C17.0217 10.1967 16.5507 10.0007 16 10C15.4493 9.99933 14.9787 10.1953 14.588 10.588C14.1973 10.9807 14.0013 11.4513 14 12C13.9987 12.5487 14.1947 13.0197 14.588 13.413C14.9813 13.8063 15.452 14.002 16 14C16.548 13.998 17.019 13.8023 17.413 13.413Z" fill="#727784"/>
                </svg>
              </FamilyAvatar>
            </FamilyAvatarGroup>
          ) : (
            <FamilyAvatarGroup>
              <FamilyAvatar $variant="single" aria-hidden>
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0.75" y="0.75" width="30.5" height="30.5" rx="15.25" fill="white"/>
                <rect x="0.75" y="0.75" width="30.5" height="30.5" rx="15.25" stroke="#C1C6D4" stroke-width="1.5"/>
                <path d="M13.175 14.825C12.3917 14.0417 12 13.1 12 12C12 10.9 12.3917 9.95833 13.175 9.175C13.9583 8.39167 14.9 8 16 8C17.1 8 18.0417 8.39167 18.825 9.175C19.6083 9.95833 20 10.9 20 12C20 13.1 19.6083 14.0417 18.825 14.825C18.0417 15.6083 17.1 16 16 16C14.9 16 13.9583 15.6083 13.175 14.825ZM8 22V21.2C8 20.6333 8.146 20.1127 8.438 19.638C8.73 19.1633 9.11733 18.8007 9.6 18.55C10.6333 18.0333 11.6833 17.646 12.75 17.388C13.8167 17.13 14.9 17.0007 16 17C17.1 16.9993 18.1833 17.1287 19.25 17.388C20.3167 17.6473 21.3667 18.0347 22.4 18.55C22.8833 18.8 23.271 19.1627 23.563 19.638C23.855 20.1133 24.0007 20.634 24 21.2V22C24 22.55 23.8043 23.021 23.413 23.413C23.0217 23.805 22.5507 24.0007 22 24H10C9.45 24 8.97933 23.8043 8.588 23.413C8.19667 23.0217 8.00067 22.5507 8 22ZM10 22H22V21.2C22 21.0167 21.9543 20.85 21.863 20.7C21.7717 20.55 21.6507 20.4333 21.5 20.35C20.6 19.9 19.6917 19.5627 18.775 19.338C17.8583 19.1133 16.9333 19.0007 16 19C15.0667 18.9993 14.1417 19.112 13.225 19.338C12.3083 19.564 11.4 19.9013 10.5 20.35C10.35 20.4333 10.229 20.55 10.137 20.7C10.045 20.85 9.99933 21.0167 10 21.2V22ZM17.413 13.413C17.8043 13.021 18 12.55 18 12C18 11.45 17.8043 10.9793 17.413 10.588C17.0217 10.1967 16.5507 10.0007 16 10C15.4493 9.99933 14.9787 10.1953 14.588 10.588C14.1973 10.9807 14.0013 11.4513 14 12C13.9987 12.5487 14.1947 13.0197 14.588 13.413C14.9813 13.8063 15.452 14.002 16 14C16.548 13.998 17.019 13.8023 17.413 13.413Z" fill="#727784"/>
              </svg>

              </FamilyAvatar>
            </FamilyAvatarGroup>
          )}
          <FamilyPlanLabel $active={isFamilyPlan}>
            {isFamilyPlan ? "Plan for 2 members" : "Plan for 1 member"}
          </FamilyPlanLabel>
        </FamilyPlanLeft>
        <FamilyPlanAction
          $active={isFamilyPlan}
          onClick={toggleFamilyPlan}
          type="button"
        >
          {isFamilyPlan ? "Remove partner" : "Add partner"}
        </FamilyPlanAction>
      </FamilyPlanToggle>
      {showSinglePlanCard ? (
        <>
          <TrialOfferCard id="committed">
            <TrialTimeline>
              <TrialTimelineItem>
                <TrialVerticalLine />
                <TrialTimelineConnector />
                <TrialTimelineIconWrapper>
                  <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1.8873" y="1.8873" width="56.2254" height="56.2254" rx="28.1127" fill="#0B6DFF" />
                    <rect x="1.8873" y="1.8873" width="56.2254" height="56.2254" rx="28.1127" stroke="white" strokeWidth="3.77461" />
                    <path d="M21.5469 23.999C21.5469 23.7338 21.6522 23.4795 21.8398 23.2919C22.0273 23.1044 22.2817 22.999 22.5469 22.999H36.5469C36.8121 22.999 37.0664 23.1044 37.254 23.2919C37.4415 23.4795 37.5469 23.7338 37.5469 23.999V27.999H21.5469V23.999Z" stroke="white" strokeWidth="2.20834" strokeLinejoin="round" />
                    <path d="M25.5391 24.499V21.499M33.5391 24.499V21.499" stroke="white" strokeWidth="2.20834" strokeLinecap="round" />
                    <path d="M21.5469 27.9985H37.5469V36.9985C37.5469 37.2638 37.4415 37.5181 37.254 37.7056C37.0664 37.8932 36.8121 37.9985 36.5469 37.9985H22.5469C22.2817 37.9985 22.0273 37.8932 21.8398 37.7056C21.6522 37.5181 21.5469 37.2638 21.5469 36.9985V27.9985Z" stroke="white" strokeWidth="2.20834" strokeLinejoin="round" />
                  </svg>
                </TrialTimelineIconWrapper>
                <TrialTimelineContent>
                  <TrialTimelineContentTop>
                    <TrialTimelineDatePill>
                      {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </TrialTimelineDatePill>
                    <TrialTimelineTitle>Today</TrialTimelineTitle>
                  </TrialTimelineContentTop>
                  <TrialTimelineDescription>
                    Get instant access to your plan with personalized workouts, nutrition and expert guidance.
                  </TrialTimelineDescription>
                </TrialTimelineContent>
              </TrialTimelineItem>
              <TrialTimelineItem>
                <TrialVerticalLine isSecond />
                <TrialTimelineConnector isSecond />
                <TrialTimelineIconWrapper>
                  <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1.42939" y="1.42939" width="57.1412" height="57.1412" rx="28.5706" fill="#418BFC" />
                    <rect x="1.42939" y="1.42939" width="57.1412" height="57.1412" rx="28.5706" stroke="white" strokeWidth="2.85879" />
                    <path d="M28.068 21.2479C28.2174 20.8786 28.4737 20.5624 28.804 20.3397C29.1344 20.117 29.5237 19.998 29.922 19.998C30.3204 19.998 30.7097 20.117 31.04 20.3397C31.3703 20.5624 31.6266 20.8786 31.776 21.2479C33.255 21.6542 34.5597 22.5349 35.4894 23.7548C36.4191 24.9747 36.9225 26.4661 36.922 27.9999V32.6969L38.754 35.4449C38.8545 35.5955 38.9122 35.7706 38.921 35.9514C38.9298 36.1322 38.8893 36.3121 38.8039 36.4717C38.7185 36.6313 38.5913 36.7648 38.436 36.8578C38.2807 36.9508 38.1031 36.9999 37.922 36.9999H33.387C33.2666 37.833 32.8501 38.5949 32.2137 39.1459C31.5774 39.6969 30.7638 40.0001 29.922 40.0001C29.0803 40.0001 28.2667 39.6969 27.6303 39.1459C26.994 38.5949 26.5774 37.833 26.457 36.9999H21.922C21.741 36.9999 21.5633 36.9508 21.408 36.8578C21.2527 36.7648 21.1256 36.6313 21.0402 36.4717C20.9547 36.3121 20.9143 36.1322 20.9231 35.9514C20.9318 35.7706 20.9895 35.5955 21.09 35.4449L22.922 32.6969V27.9999C22.922 24.7759 25.102 22.0599 28.068 21.2479ZM28.508 36.9999C28.6113 37.2926 28.8028 37.546 29.0561 37.7252C29.3095 37.9045 29.6122 38.0007 29.9225 38.0007C30.2329 38.0007 30.5356 37.9045 30.7889 37.7252C31.0422 37.546 31.2337 37.2926 31.337 36.9999H28.508ZM29.922 22.9999C28.5959 22.9999 27.3242 23.5267 26.3865 24.4644C25.4488 25.4021 24.922 26.6738 24.922 27.9999V32.9999C24.9221 33.1975 24.8636 33.3906 24.754 33.5549L23.791 34.9999H36.052L35.089 33.5549C34.9798 33.3905 34.9217 33.1974 34.922 32.9999V27.9999C34.922 26.6738 34.3952 25.4021 33.4576 24.4644C32.5199 23.5267 31.2481 22.9999 29.922 22.9999Z" fill="white" />
                  </svg>
                </TrialTimelineIconWrapper>
                <TrialTimelineContent>
                  <TrialTimelineContentTop>
                    <TrialTimelineDatePill>
                      {(() => {
                        const d = new Date();
                        d.setDate(d.getDate() + (isNoTrial ? 7 : 12));
                        return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                      })()}
                    </TrialTimelineDatePill>
                    <TrialTimelineTitle>{isNoTrial ? "Day 7" : "Day 12"}</TrialTimelineTitle>
                  </TrialTimelineContentTop>
                  <TrialTimelineDescription>
                    {isNoTrial
                      ? "Your plan adjusts every week based on how your body is changing, so you're never stuck doing something that isn't working."
                      : "We'll send you a quick email reminder before your free trial ends, so you know it's coming up."}
                  </TrialTimelineDescription>
                </TrialTimelineContent>
              </TrialTimelineItem>
              <TrialTimelineItem>
                <TrialTimelineIconWrapper>
                  <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1.42939" y="1.42939" width="57.1412" height="57.1412" rx="28.5706" fill="#8EB6F8" />
                    <rect x="1.42939" y="1.42939" width="57.1412" height="57.1412" rx="28.5706" stroke="white" strokeWidth="2.85879" />
                    <path d="M30.6711 21.0005C30.8567 21.0006 31.0387 21.0524 31.1966 21.15C31.3545 21.2477 31.4821 21.3874 31.5651 21.5535L32.2891 23.0005H38.2891C38.5543 23.0005 38.8086 23.1058 38.9962 23.2934C39.1837 23.4809 39.2891 23.7353 39.2891 24.0005V35.0005C39.2891 35.2657 39.1837 35.5201 38.9962 35.7076C38.8086 35.8951 38.5543 36.0005 38.2891 36.0005H31.9071C31.7214 36.0004 31.5394 35.9486 31.3815 35.8509C31.2236 35.7533 31.0961 35.6136 31.0131 35.4475L30.2891 34.0005H23.2891V40.0005H21.2891V21.0005H30.6711ZM30.0531 23.0005H23.2891V32.0005H31.5251L32.5251 34.0005H37.2891V25.0005H31.0531L30.0531 23.0005Z" fill="white" />
                  </svg>
                </TrialTimelineIconWrapper>
                <TrialTimelineContent>
                  <TrialTimelineContentTop>
                    <TrialTimelineDatePill>
                      {(() => {
                        const d = new Date();
                        d.setDate(d.getDate() + (isNoTrial ? 30 : 14));
                        return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                      })()}
                    </TrialTimelineDatePill>
                    <TrialTimelineTitle>{isNoTrial ? "Day 30" : "Day 14"}</TrialTimelineTitle>
                  </TrialTimelineContentTop>
                  <TrialTimelineDescription>
                    {isNoTrial
                      ? "If you don't see real results within 30 days, we'll refund you every penny back. No questions asked."
                      : "If you choose to continue, you'll be charged the subscription amount. Cancel anytime before."}
                  </TrialTimelineDescription>
                </TrialTimelineContent>
              </TrialTimelineItem>
            </TrialTimeline>
          </TrialOfferCard>
          <TrialOfferCard>
            <PricingWrapper $isFamilyPlan={isFamilyPlan}>
              <TrialPricingSection $isFamilyPlan={isFamilyPlan}>
                {isNoTrial ? (
                  <>
                    <DiscountRow>
                      {!isFamilyPlan && <Pill $theme="green">20% OFF</Pill>}
                      <PriceCluster>
                        <TrialPricingAmount>
                          {discountedPrice(yearlyPlan)}
                          {yearlyPlan.priceDetails}
                        </TrialPricingAmount>
                        <TrialPricingDetail> {isFamilyPlan ? "per person" : "(billed annually)"}</TrialPricingDetail>
                      </PriceCluster>
                    </DiscountRow>
                  </>
                ) : (
                  <>
                    <TrialPricingLabel>14-day trial, then</TrialPricingLabel>
                    <TrialPricingAmount>
                      {yearlyPlan.price}
                      {yearlyPlan.priceDetails}
                    </TrialPricingAmount>
                    <TrialPricingDetail> {isFamilyPlan ? "per person" : "(billed annually)"}</TrialPricingDetail>
                  </>
                )}
                {yearlyPlan.fullPriceForBoth ? <FamilyPlanPricingDetail>(Billed annually at ${isNoTrial ? applyDiscount(yearlyPlan.fullPriceForBoth, false) : yearlyPlan.fullPriceForBoth} total)</FamilyPlanPricingDetail> : null}
              </TrialPricingSection>
              {isFamilyPlan && (
                <Pills>
                  <Pill $theme="blue">2 MEMBERS</Pill>
                  {yearlyPlan.tagSavePercentage ? <Pill $theme="green">SAVE {isNoTrial ? `${savingsPercent(yearlyPlan)}%` : yearlyPlan.tagSavePercentage}</Pill> : null}
                </Pills>
              )}
            </PricingWrapper>
            {isFamilyPlan && (
              <VsSeparatePlans>
                vs. $15.99/mo each on separate plans
              </VsSeparatePlans>
            )}
          </TrialOfferCard>
          {isNoTrial && (
            <SavingsLine>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 6L9 17L4 12" stroke="#317A40" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              You save {savingsPercent(yearlyPlan)}% on your first year by paying today.
            </SavingsLine>
          )}
          {trialToggleRow}
          <TrialCtaWrapper>
            <CheckoutButton
              href={checkoutUrlFor(yearlyPlan)}
              onClick={(e) => {
                e.preventDefault();
                navigateWithInitiateCheckout(checkoutUrlFor(yearlyPlan));
              }}
              $isDisabled={false}
              target="_self"
              rel="noopener"
            >
              {isNoTrial ? "Get My Plan" : "Start Free Trial"}
            </CheckoutButton>
            <TrialViewAllPlans
              data-hero-scroll-anchor="pricing-view-all-plans"
              onClick={() => setShowSinglePlanCard(false)}
            >
              View all plans
            </TrialViewAllPlans>
          </TrialCtaWrapper>
        </>
      ) : (
        <>
        <PlansWrapper>
          {(isFamilyPlan ? multiUserPlans : plans).map((plan) => {
            const mode = localStorage.getItem("mode");

            if ((mode === "inapp" || mode === "webview") && plan.id === "base") {
              return null;
            }

            return (
              <div style={{ flex: 1 }} id={plan.id}>
                <PlanCard key={plan.id} $isSelected={false} $hasBadge={!!plan.badge}>
                  <Section1>
                    {plan.id === "committed" ? (
                      <PlanBadgeWeb13>
                        {plan.badge}
                        <PlanIconPlus>
                          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10.1465 3.79102H17.8535C18.0635 3.79102 18.2443 3.79102 18.4077 3.81085C18.993 3.88375 19.5367 4.15166 19.9511 4.57138C20.3655 4.99109 20.6265 5.53816 20.692 6.12435H21.6323C21.8797 6.12202 22.4222 6.11735 22.8702 6.41718C23.45 6.80335 23.625 7.46718 23.625 8.16602C23.625 11.3464 21.9298 13.4697 19.7657 14.6492C18.5582 16.8472 16.3275 18.3744 14 18.3744C12.2278 18.3744 10.5467 17.4154 9.33683 16.0889C8.93483 15.6503 8.58196 15.1691 8.2845 14.6539C6.1215 13.4802 4.375 11.3592 4.375 8.16602C4.375 7.46602 4.55 6.80335 5.12867 6.41718C5.57783 6.11735 6.12033 6.12202 6.36767 6.12435H7.308L7.3115 6.09168C7.38363 5.5119 7.64713 4.97274 8.06026 4.55961C8.47339 4.14648 9.01255 3.88298 9.59233 3.81085C9.75567 3.79102 9.9365 3.79102 10.1465 3.79102ZM9.04167 6.99935V7.00285L9.03 11.0827V11.0839C9.03 11.925 9.27033 12.7849 9.68683 13.5724L9.70317 13.6015C9.954 14.0705 10.2678 14.5115 10.6307 14.9082C11.613 15.9862 12.8567 16.6244 14.0012 16.6244C15.6602 16.6244 17.409 15.425 18.3178 13.6564C18.7307 12.8612 18.9467 11.9786 18.9478 11.0827L18.9455 11.0804L18.9595 6.69252C18.9595 6.41018 18.9572 6.34835 18.9525 6.30752C18.9283 6.11451 18.8405 5.93506 18.703 5.79752C18.5655 5.65998 18.386 5.57218 18.193 5.54802C18.0648 5.54039 17.9364 5.53805 17.808 5.54102H10.1932C9.90967 5.54102 9.849 5.54218 9.80817 5.54802C9.61516 5.57218 9.43571 5.65998 9.29817 5.79752C9.16063 5.93506 9.07283 6.11451 9.04867 6.30752C9.04104 6.4357 9.0387 6.56414 9.04167 6.69252V6.99935ZM20.706 7.87435L20.6955 11.0827V11.0839M20.6687 11.7127C21.413 10.8167 21.8762 9.64768 21.8762 8.16602C21.8762 8.02835 21.8645 7.93735 21.854 7.88018C21.7643 7.87478 21.6744 7.87283 21.5845 7.87435H20.706M7.30333 11.645C7.28573 11.4581 7.27717 11.2704 7.27767 11.0827V11.0804L7.28817 7.87435H6.41667C6.2895 7.87435 6.209 7.87435 6.14717 7.88018C6.13077 7.97455 6.12335 8.07025 6.125 8.16602C6.125 9.60918 6.57767 10.756 7.30333 11.645ZM14 18.9577C14.2321 18.9577 14.4546 19.0499 14.6187 19.214C14.7828 19.3781 14.875 19.6006 14.875 19.8327V22.4577H18.6667C18.8987 22.4577 19.1213 22.5499 19.2854 22.714C19.4495 22.8781 19.5417 23.1006 19.5417 23.3327C19.5417 23.5648 19.4495 23.7873 19.2854 23.9514C19.1213 24.1155 18.8987 24.2077 18.6667 24.2077H9.33333C9.10127 24.2077 8.87871 24.1155 8.71461 23.9514C8.55052 23.7873 8.45833 23.5648 8.45833 23.3327C8.45833 23.1006 8.55052 22.8781 8.71461 22.714C8.87871 22.5499 9.10127 22.4577 9.33333 22.4577H13.125V19.8327C13.125 19.6006 13.2172 19.3781 13.3813 19.214C13.5454 19.0499 13.7679 18.9577 14 18.9577Z" fill="#0B6DFF" />
                            <path d="M13.7308 7.64806C13.7533 7.59545 13.7906 7.55058 13.8383 7.51905C13.8861 7.48752 13.942 7.4707 13.9992 7.4707C14.0564 7.4707 14.1123 7.48752 14.16 7.51905C14.2077 7.55058 14.2451 7.59545 14.2675 7.64806L14.8567 9.06206C14.8772 9.11141 14.911 9.15416 14.9542 9.18564C14.9974 9.21712 15.0484 9.23612 15.1017 9.24056L16.6288 9.36306C16.6863 9.36761 16.7411 9.38907 16.7864 9.42474C16.8316 9.46041 16.8653 9.50869 16.8831 9.56348C16.901 9.61827 16.9022 9.67712 16.8866 9.73261C16.8711 9.78809 16.8394 9.83772 16.7957 9.87523L15.6325 10.8716C15.5918 10.9065 15.5616 10.9521 15.5452 11.0031C15.5287 11.0542 15.5266 11.1087 15.5392 11.1609L15.8938 12.6496C15.9075 12.7057 15.9042 12.7647 15.8843 12.819C15.8644 12.8733 15.8288 12.9204 15.782 12.9544C15.7352 12.9884 15.6794 13.0078 15.6216 13.01C15.5639 13.0122 15.5067 12.9972 15.4575 12.9669L14.1508 12.1689C14.1051 12.1411 14.0527 12.1264 13.9992 12.1264C13.9457 12.1264 13.8932 12.1411 13.8475 12.1689L12.5408 12.9669C12.4916 12.9972 12.4345 13.0122 12.3767 13.01C12.3189 13.0078 12.2631 12.9884 12.2163 12.9544C12.1696 12.9204 12.134 12.8733 12.1141 12.819C12.0942 12.7647 12.0908 12.7057 12.1045 12.6496L12.4603 11.1609C12.4729 11.1087 12.4708 11.0542 12.4543 11.0031C12.4379 10.9521 12.4077 10.9065 12.367 10.8716L11.2027 9.87523C11.1585 9.8378 11.1265 9.78809 11.1107 9.73242C11.0949 9.67675 11.096 9.61764 11.1139 9.5626C11.1318 9.50756 11.1656 9.45909 11.2112 9.42335C11.2567 9.38761 11.3118 9.36621 11.3695 9.36189L12.8955 9.24056C12.949 9.23632 13.0003 9.21742 13.0437 9.18593C13.0871 9.15444 13.121 9.11158 13.1417 9.06206L13.7308 7.64806Z" fill="#0B6DFF" />
                          </svg>
                        </PlanIconPlus>
                      </PlanBadgeWeb13>
                    ) : (
                      plan.icon ? <PlanIcon>{plan.icon}</PlanIcon> : null
                    )}
                  </Section1>
                  <Section2>
                    <CardTitle $hasBadge={!!plan.badge} $isSelected={false}>
                      {plan.title}
                    </CardTitle>
                  </Section2>
                  <Section5>
                    <PlanDescription>{plan.description}</PlanDescription>
                  </Section5>
                  <Section4>
                    <PriceWrap>
                      <PlanPrice>
                        <PriceAmount>{isNoTrial ? discountedPrice(plan) : (plan.price ?? "")}</PriceAmount>
                        {plan.priceDetails && <PriceDetails>{plan.priceDetails}</PriceDetails>}
                      </PlanPrice>
                    </PriceWrap>
                    {plan.billingDetails && (
                      <PlanBillingDetails>
                        {isNoTrial && plan.fullPriceForBoth
                          ? plan.billingDetails.replace(`$${plan.fullPriceForBoth}`, applyDiscount(plan.fullPriceForBoth))
                          : plan.billingDetails}
                      </PlanBillingDetails>
                    )}
                    {isNoTrial && !isFamilyPlan && (
                      <Pills $direction="row"><Pill $theme="green">20% OFF</Pill></Pills>
                    )}
                  </Section4>

                  {isFamilyPlan && (
                    <FamilyPlanPills $direction="row">
                      <Pill $theme="blue">2 MEMBERS</Pill>
                      {plan.tagSavePercentage ? <Pill $theme={plan.tagTheme ?? "green"}>SAVE {isNoTrial ? `${savingsPercent(plan)}%` : plan.tagSavePercentage}</Pill> : null}
                    </FamilyPlanPills>
                  )}
                  
                  <Section6>
                    <ButtonContainer>
                      <CheckoutButton
                        href={checkoutUrlFor(plan)}
                        onClick={(e) => {
                          e.preventDefault();
                          navigateWithInitiateCheckout(checkoutUrlFor(plan));
                        }}
                        $isDisabled={false}
                        target="_self"
                        rel="noopener"
                      >
                        {isNoTrial ? "Get My Plan" : "Start Free Trial"}
                      </CheckoutButton>
                      <TrialText>{isNoTrial ? "30-Day Money-Back Guarantee" : "14 Day Free Trial. Cancel Anytime"}</TrialText>
                    </ButtonContainer>
                  </Section6>
                </PlanCard>
              </div>
            );
          })}
        </PlansWrapper>
        {trialToggleRow}
        {isTrialToggleVariation && (
          <BackToPlanLink onClick={() => setShowSinglePlanCard(true)}>
            ← Back to recommended plan
          </BackToPlanLink>
        )}
        </>
      )}
    </SectionContainer>
  );
});

export default PricingPlans;
